import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';

export default function AcceptTransferPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transferDetails, setTransferDetails] = useState<any>(null);
  const [accepting, setAccepting] = useState(false);
  const [createNewAccount, setCreateNewAccount] = useState(false);
  const [accountName, setAccountName] = useState('');

  useEffect(() => {
    if (!token) {
      setError('No transfer token provided');
      setLoading(false);
      return;
    }

    fetchTransferDetails();
  }, [token]);

  const fetchTransferDetails = async () => {
    try {
      const response = await fetch(`/api/orgs/transfer?token=${token}`);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch transfer details');
      }

      setTransferDetails(result);
    } catch (err: any) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load transfer details');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      // Save token and redirect to login
      sessionStorage.setItem('pendingTransferToken', token || '');
      navigate('/login');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch('/api/orgs/transfer', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          token,
          createNewAccount,
          accountName: createNewAccount ? accountName : undefined
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to accept transfer');
      }

      // Success - redirect to org page
      navigate(`/flows?orgId=${result.orgId}`, {
        state: { 
          showAuthPrompt: true,
          message: 'Organization transferred successfully! Please authenticate with Salesforce to enable syncing.'
        }
      });

    } catch (err: any) {
      console.error('Accept error:', err);
      setError(err.message || 'Failed to accept transfer');
      setAccepting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <p className="mt-2 text-gray-600">Loading transfer details...</p>
        </div>
      </div>
    );
  }

  if (error && !transferDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Transfer Error</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">Accept Organization Transfer</h1>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {transferDetails && (
              <div className="space-y-6">
                {/* Org Details */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Organization Details</h3>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Organization Name</dt>
                      <dd className="mt-1 text-sm text-gray-900">{transferDetails.org.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Instance URL</dt>
                      <dd className="mt-1 text-sm text-gray-900">{transferDetails.org.instanceUrl}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">From Account</dt>
                      <dd className="mt-1 text-sm text-gray-900">{transferDetails.org.fromAccount}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Initiated By</dt>
                      <dd className="mt-1 text-sm text-gray-900">{transferDetails.transfer.initiatedBy}</dd>
                    </div>
                  </dl>
                </div>

                {/* Transfer Note */}
                {transferDetails.org.transferNote && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Transfer Note</h3>
                    <div className="mt-1 bg-gray-50 rounded-md p-3">
                      <p className="text-sm text-gray-700">{transferDetails.org.transferNote}</p>
                    </div>
                  </div>
                )}

                {/* Expiration Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Transfer expires soon</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>This transfer expires on {new Date(transferDetails.org.expiresAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Section */}
                {user ? (
                  <div>
                    {error && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    )}

                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={createNewAccount}
                            onChange={(e) => setCreateNewAccount(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">Create a new account for this org</span>
                        </label>
                      </div>

                      {createNewAccount && (
                        <div>
                          <label htmlFor="account-name" className="block text-sm font-medium text-gray-700">
                            Account Name
                          </label>
                          <input
                            type="text"
                            id="account-name"
                            value={accountName}
                            onChange={(e) => setAccountName(e.target.value)}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="My Company Account"
                          />
                        </div>
                      )}

                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => navigate('/')}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          disabled={accepting}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAccept}
                          disabled={accepting || (createNewAccount && !accountName)}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {accepting ? 'Accepting Transfer...' : 'Accept Transfer'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Please log in or create an account to accept this transfer.
                    </p>
                    <div className="space-x-3">
                      <button
                        onClick={() => {
                          sessionStorage.setItem('pendingTransferToken', token || '');
                          navigate('/login');
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        Log In
                      </button>
                      <button
                        onClick={() => {
                          sessionStorage.setItem('pendingTransferToken', token || '');
                          navigate('/signup');
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Create Account
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}